import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Container, Form, InputGroup, Modal, Nav, Spinner, Table, Tab } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';

/**
 * /admin/billing — Admin dashboard for Phase 1 Stripe subscription system.
 * Single-page tabbed layout keeping the surface small:
 *   • Plans (CRUD + discount fields)
 *   • Subscriptions (list + sync + cancel)
 *   • Assignment Requests (approve → assign candidate)
 *   • Webhook Events (audit log)
 */
export default function BillingAdminPage() {
  const toast = useToast();
  const [tab, setTab] = useState('plans');
  return (
    <Container fluid className="py-3">
      <PageHeader title="Subscription Billing" subtitle="Plans, subscriptions, assignments & webhook events" />
      <Tab.Container activeKey={tab} onSelect={setTab}>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item><Nav.Link eventKey="plans" data-testid="tab-plans">Plans</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="subs" data-testid="tab-subs">Subscriptions</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="assignments" data-testid="tab-assignments">Assignment Requests</Nav.Link></Nav.Item>
          <Nav.Item><Nav.Link eventKey="webhooks" data-testid="tab-webhooks">Webhook Events</Nav.Link></Nav.Item>
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey="plans"><PlansTab toast={toast} /></Tab.Pane>
          <Tab.Pane eventKey="subs"><SubsTab toast={toast} /></Tab.Pane>
          <Tab.Pane eventKey="assignments"><AssignmentsTab toast={toast} /></Tab.Pane>
          <Tab.Pane eventKey="webhooks"><WebhookTab /></Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Plans tab
// ---------------------------------------------------------------------------
function PlansTab({ toast }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | plan object

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get('/admin/subscription-plans');
      setPlans(r.data?.data || []);
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    if (!window.confirm(`Delete plan "${p.name}"?`)) return;
    try { await adminApi.delete(`/admin/subscription-plans/${p.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  if (loading) return <div className="text-center py-4"><Spinner /></div>;
  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="text-muted small">{plans.length} plan(s)</div>
        <Button size="sm" onClick={() => setEditing({})} data-testid="new-plan-btn"><i className="bi bi-plus-lg me-1" />New Plan</Button>
      </div>
      {plans.length === 0 ? (
        <Alert variant="light" className="text-center">No plans yet. Click "New Plan" to create one.</Alert>
      ) : (
        <Table hover responsive size="sm">
          <thead><tr><th>Name</th><th>Price</th><th>Interval</th><th>Stripe Price ID</th><th>Discount</th><th>Featured</th><th>Active</th><th className="text-end">Actions</th></tr></thead>
          <tbody>
            {plans.map(p => (
              <tr key={p.id} data-testid={`plan-row-${p.id}`}>
                <td>{p.name}</td>
                <td>${p.effective_price}{p.discount_currently_valid && p.effective_price !== p.regular_price && <><br /><small className="text-muted text-decoration-line-through">${p.regular_price}</small></>}</td>
                <td>{p.billing_interval}</td>
                <td><code className="small">{p.stripe_price_id || <span className="text-danger">— not set —</span>}</code></td>
                <td>{p.discount_currently_valid ? <Badge bg="success">{p.discount_label || 'Active'}</Badge> : <span className="text-muted small">—</span>}</td>
                <td>{p.featured ? <Badge bg="warning" text="dark">Featured</Badge> : ''}</td>
                <td>{p.active ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>}</td>
                <td className="text-end">
                  <Button size="sm" variant="outline-primary" className="me-1" onClick={() => setEditing(p)} data-testid={`edit-plan-${p.id}`}>Edit</Button>
                  <Button size="sm" variant="outline-danger" onClick={() => remove(p)} data-testid={`delete-plan-${p.id}`}>×</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {editing !== null && <PlanFormModal plan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} toast={toast} />}
    </>
  );
}

function PlanFormModal({ plan, onClose, onSaved, toast }) {
  const isNew = !plan?.id;
  const [f, setF] = useState(() => {
    const base = {
      name: '', description: '', features: '', regular_price: 0, currency: 'usd',
      billing_interval: 'month', stripe_price_id: '', active: true, display_order: 0, featured: false,
      discount_active: false, discounted_price: '', discount_percentage: '', discount_label: '',
      discount_start_date: '', discount_end_date: '', discounted_stripe_price_id: '',
      stripe_coupon_id: '', stripe_promotion_code: '',
    };
    const merged = { ...base, ...(plan || {}) };
    // features may come from the API as an array; normalise to a newline-joined string for the textarea.
    merged.features = Array.isArray(plan?.features)
      ? plan.features.join('\n')
      : (plan?.features || '');
    return merged;
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, features: (f.features || '').split('\n').map(x => x.trim()).filter(Boolean),
        regular_price: Number(f.regular_price) || 0,
        discounted_price: f.discounted_price === '' ? null : Number(f.discounted_price),
        discount_percentage: f.discount_percentage === '' ? null : Number(f.discount_percentage),
        display_order: Number(f.display_order) || 0,
      };
      if (isNew) await adminApi.post('/admin/subscription-plans', body);
      else await adminApi.put(`/admin/subscription-plans/${plan.id}`, body);
      toast.success('Plan saved');
      onSaved();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Modal show onHide={onClose} size="lg" data-testid="plan-form-modal">
      <Modal.Header closeButton><Modal.Title>{isNew ? 'New' : 'Edit'} Plan</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form>
          <div className="row g-2">
            <div className="col-md-8"><Form.Label>Name</Form.Label><Form.Control value={f.name} onChange={e => setF({...f, name: e.target.value})} data-testid="plan-name-input" /></div>
            <div className="col-md-4"><Form.Label>Order</Form.Label><Form.Control type="number" value={f.display_order} onChange={e => setF({...f, display_order: e.target.value})} /></div>
            <div className="col-12"><Form.Label>Description</Form.Label><Form.Control as="textarea" rows={2} value={f.description} onChange={e => setF({...f, description: e.target.value})} /></div>
            <div className="col-12"><Form.Label>Features (one per line)</Form.Label><Form.Control as="textarea" rows={3} value={f.features} onChange={e => setF({...f, features: e.target.value})} /></div>
            <div className="col-md-4"><Form.Label>Regular Price</Form.Label><Form.Control type="number" step="0.01" value={f.regular_price} onChange={e => setF({...f, regular_price: e.target.value})} data-testid="plan-price-input" /></div>
            <div className="col-md-4"><Form.Label>Interval</Form.Label><Form.Select value={f.billing_interval} onChange={e => setF({...f, billing_interval: e.target.value})}><option value="month">month</option><option value="year">year</option><option value="week">week</option><option value="one_time">one_time</option></Form.Select></div>
            <div className="col-md-4"><Form.Label>Currency</Form.Label><Form.Control value={f.currency} onChange={e => setF({...f, currency: e.target.value})} /></div>
            <div className="col-12"><Form.Label>Stripe Price ID</Form.Label><Form.Control value={f.stripe_price_id} onChange={e => setF({...f, stripe_price_id: e.target.value})} placeholder="price_..." data-testid="plan-stripe-price-input" /></div>
            <div className="col-md-4"><Form.Check type="switch" label="Active" checked={f.active} onChange={e => setF({...f, active: e.target.checked})} /></div>
            <div className="col-md-4"><Form.Check type="switch" label="Featured" checked={f.featured} onChange={e => setF({...f, featured: e.target.checked})} /></div>
          </div>
          <hr />
          <Form.Check type="switch" label="Enable discount" checked={f.discount_active} onChange={e => setF({...f, discount_active: e.target.checked})} data-testid="plan-discount-toggle" />
          {f.discount_active && (
            <div className="row g-2 mt-1">
              <div className="col-md-6"><Form.Label>Discounted Price</Form.Label><Form.Control type="number" step="0.01" value={f.discounted_price} onChange={e => setF({...f, discounted_price: e.target.value})} /></div>
              <div className="col-md-6"><Form.Label>Discount %</Form.Label><Form.Control type="number" step="1" value={f.discount_percentage} onChange={e => setF({...f, discount_percentage: e.target.value})} /></div>
              <div className="col-md-6"><Form.Label>Discount Label</Form.Label><Form.Control value={f.discount_label} onChange={e => setF({...f, discount_label: e.target.value})} placeholder="Save 20%" /></div>
              <div className="col-md-3"><Form.Label>Start Date</Form.Label><Form.Control type="date" value={f.discount_start_date} onChange={e => setF({...f, discount_start_date: e.target.value})} /></div>
              <div className="col-md-3"><Form.Label>End Date</Form.Label><Form.Control type="date" value={f.discount_end_date} onChange={e => setF({...f, discount_end_date: e.target.value})} /></div>
              <div className="col-md-4"><Form.Label>Discounted Stripe Price ID</Form.Label><Form.Control value={f.discounted_stripe_price_id || ''} onChange={e => setF({...f, discounted_stripe_price_id: e.target.value})} placeholder="price_..." /></div>
              <div className="col-md-4"><Form.Label>OR Stripe Coupon ID</Form.Label><Form.Control value={f.stripe_coupon_id || ''} onChange={e => setF({...f, stripe_coupon_id: e.target.value})} /></div>
              <div className="col-md-4"><Form.Label>OR Promotion Code</Form.Label><Form.Control value={f.stripe_promotion_code || ''} onChange={e => setF({...f, stripe_promotion_code: e.target.value})} /></div>
            </div>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={saving} data-testid="plan-save-btn">{saving && <Spinner size="sm" className="me-1" />}Save</Button>
      </Modal.Footer>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Subscriptions tab
// ---------------------------------------------------------------------------
function SubsTab({ toast }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [query, setQuery] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await adminApi.get('/admin/subscriptions', { params: { status: statusFilter || undefined, q: query } }); setSubs(r.data?.data || []); }
    catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  }, [toast, statusFilter, query]);
  useEffect(() => { load(); }, [load]);
  const sync = async (id) => { try { await adminApi.post(`/admin/subscriptions/${id}/sync`); toast.success('Synced'); load(); } catch (e) { toast.error(extractError(e)); } };
  const cancel = async (id) => { if (!window.confirm('Cancel this subscription at period end?')) return; try { await adminApi.post(`/admin/subscriptions/${id}/cancel`); toast.success('Cancellation queued'); load(); } catch (e) { toast.error(extractError(e)); } };

  return (
    <>
      <div className="d-flex gap-2 mb-3">
        <InputGroup style={{ maxWidth: 320 }}><InputGroup.Text><i className="bi bi-search" /></InputGroup.Text><Form.Control placeholder="Search email or Stripe id" value={query} onChange={e => setQuery(e.target.value)} data-testid="subs-search" /></InputGroup>
        <Form.Select style={{ maxWidth: 200 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="subs-status-filter">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="past_due">Past Due</option>
          <option value="unpaid">Unpaid</option>
          <option value="canceled">Cancelled</option>
        </Form.Select>
      </div>
      {loading ? <div className="text-center py-4"><Spinner /></div> : (
        subs.length === 0 ? <Alert variant="light" className="text-center">No subscriptions yet.</Alert> :
        <Table hover responsive size="sm">
          <thead><tr><th>Customer</th><th>Plan</th><th className="text-end">Amount</th><th>Assigned candidate(s)</th><th>Status</th><th>Next renewal / ends</th><th>Stripe</th><th className="text-end">Actions</th></tr></thead>
          <tbody>
            {subs.map(s => (
              <tr key={s.id} data-testid={`sub-row-${s.id}`}>
                <td><div>{s.user_name || '—'}</div><small className="text-muted">{s.user_email}</small></td>
                <td>{s.plan_name} <small className="text-muted">({s.billing_interval})</small></td>
                <td className="text-end" data-testid={`sub-amount-${s.id}`}>
                  {s.plan_amount != null
                    ? <span>{new Intl.NumberFormat('en-US', { style: 'currency', currency: (s.currency || 'usd').toUpperCase() }).format(Number(s.plan_amount))}<small className="text-muted d-block">/{s.billing_interval}</small></span>
                    : <span className="text-muted">—</span>}
                </td>
                <td data-testid={`sub-candidates-${s.id}`}>
                  {(s.assigned_candidates || []).length === 0 ? (
                    <small className="text-muted fst-italic">Not assigned</small>
                  ) : (
                    <div className="d-flex flex-column gap-1">
                      {s.assigned_candidates.map(c => (
                        <a key={c.id} href={`/candidates/${c.id}`} className="text-decoration-none small" data-testid={`sub-candidate-link-${s.id}-${c.id}`}>
                          <i className="bi bi-person-badge me-1" />{c.full_name}
                        </a>
                      ))}
                    </div>
                  )}
                </td>
                <td><Badge bg={s.status === 'active' ? 'success' : s.status === 'canceled' ? 'dark' : s.status === 'past_due' ? 'warning' : 'secondary'}>{s.status || 'unknown'}</Badge>{s.cancel_at_period_end && <div><small className="text-warning fw-semibold">Auto-renew off</small></div>}</td>
                <td>
                  <div><small>{s.current_period_end?.slice(0, 10) || '—'}</small></div>
                  <small className="text-muted">{s.cancel_at_period_end ? 'ends' : 'renews'}</small>
                </td>
                <td><small><code>{s.stripe_subscription_id?.slice(-10)}</code></small></td>
                <td className="text-end">
                  <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => sync(s.stripe_subscription_id)} data-testid={`sync-sub-${s.id}`}>Sync</Button>
                  <Button size="sm" variant="outline-danger" onClick={() => cancel(s.stripe_subscription_id)} data-testid={`cancel-sub-${s.id}`}>Cancel</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Assignment Requests tab
// ---------------------------------------------------------------------------
function AssignmentsTab({ toast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignInput, setAssignInput] = useState({});
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await adminApi.get('/admin/assignment-requests'); setItems(r.data?.data || []); }
    catch (e) { toast.error(extractError(e)); } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);
  const approve = async (id) => {
    const cid = (assignInput[id] || '').trim();
    if (!cid) return toast.error('Enter a candidate id first');
    try { await adminApi.post(`/admin/assignment-requests/${id}/approve`, { candidate_id: cid }); toast.success('Assignment approved'); load(); }
    catch (e) { toast.error(extractError(e)); }
  };
  if (loading) return <div className="text-center py-4"><Spinner /></div>;
  if (!items.length) return <Alert variant="light" className="text-center">No pending assignment requests.</Alert>;
  return (
    <Table hover responsive size="sm">
      <thead><tr><th>Email</th><th>Note</th><th>Created</th><th style={{ minWidth: 280 }}>Approve → Candidate ID</th></tr></thead>
      <tbody>
        {items.map(x => (
          <tr key={x.id}>
            <td>{x.email}</td>
            <td className="small">{x.candidate_note}</td>
            <td className="small text-muted">{x.created_at?.slice(0, 10)}</td>
            <td>
              <InputGroup size="sm">
                <Form.Control placeholder="Paste candidate uuid" value={assignInput[x.id] || ''} onChange={e => setAssignInput({...assignInput, [x.id]: e.target.value})} />
                <Button variant="primary" onClick={() => approve(x.id)}>Approve</Button>
              </InputGroup>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

// ---------------------------------------------------------------------------
// Webhook Events tab
// ---------------------------------------------------------------------------
function WebhookTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => {
    try { const r = await adminApi.get('/admin/webhook-events'); setItems(r.data?.data || []); }
    finally { setLoading(false); }
  })(); }, []);
  if (loading) return <div className="text-center py-4"><Spinner /></div>;
  if (!items.length) return <Alert variant="light" className="text-center">No webhook events received yet.</Alert>;
  return (
    <Table hover size="sm">
      <thead><tr><th>Event</th><th>Type</th><th>Status</th><th>Received</th></tr></thead>
      <tbody>
        {items.map(x => (
          <tr key={x.id}>
            <td><code className="small">{x.id?.slice(0, 20)}</code></td>
            <td>{x.event_type}</td>
            <td>{x.error ? <Badge bg="danger" title={x.error}>error</Badge> : x.processed ? <Badge bg="success">processed</Badge> : <Badge bg="secondary">pending</Badge>}</td>
            <td className="small text-muted">{x.created_at?.replace('T', ' ').slice(0, 19)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
